import React, { useState, useEffect } from 'react';
import { TRAINING_LESSONS } from './constants';
import { getTrainingLessonContent } from '@common/services/geminiService';
import Loader from './Loader';
import { createSafeMarkdown } from '../utils/safeMarkdown';

const md = createSafeMarkdown();

interface TrainingPageProps {
    onNavigateHome: () => void;
}

const TrainingPage: React.FC<TrainingPageProps> = ({ onNavigateHome }) => {
    const [selectedLesson, setSelectedLesson] = useState(TRAINING_LESSONS[0]);
    const [lessonContent, setLessonContent] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchContent = async () => {
            setIsLoading(true);
            try {
                const content = await getTrainingLessonContent(selectedLesson);
                setLessonContent(content);
            } catch (error) {
                console.error("Failed to fetch lesson content:", error);
                setLessonContent("Could not load lesson content. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchContent();
    }, [selectedLesson]);

    const difficultyColor = {
        Beginner: 'bg-green-100 text-green-800',
        Intermediate: 'bg-yellow-100 text-yellow-800',
        Advanced: 'bg-red-100 text-red-800',
    };

    return (
        <div className="animate-fade-in">
             <button
                onClick={onNavigateHome}
                className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 mb-6 transition-colors duration-200"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Knowledge Hub
            </button>
            <div className="text-center mb-10">
                <h1 className="text-4xl font-bold text-slate-800">IntelOptics Training Center</h1>
                <p className="text-slate-500 mt-2 max-w-2xl mx-auto">Welcome to your self-paced learning portal. Master the platform and enhance your intelligence capabilities.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {/* Lesson Sidebar */}
                <div className="md:col-span-1">
                    <h2 className="text-lg font-semibold text-slate-700 mb-4">Course Curriculum</h2>
                    <div className="space-y-2">
                        {TRAINING_LESSONS.map(lesson => (
                            <button
                                key={lesson.id}
                                onClick={() => setSelectedLesson(lesson)}
                                className={`w-full text-left p-3 rounded-md transition-colors text-sm ${selectedLesson.id === lesson.id ? 'bg-blue-600 text-white font-semibold' : 'bg-white hover:bg-slate-100 text-slate-700'}`}
                            >
                                {lesson.title}
                                <div className={`mt-1 text-xs inline-block px-2 py-0.5 rounded-full ${difficultyColor[lesson.difficulty]} ${selectedLesson.id === lesson.id ? 'bg-opacity-20 text-white' : ''}`}>
                                    {lesson.difficulty}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Lesson Content */}
                <div className="md:col-span-3 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div>
                            <div className="flex justify-between items-center mb-4 border-b pb-4">
                                <h2 className="text-2xl font-bold text-slate-800">{selectedLesson.title}</h2>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${difficultyColor[selectedLesson.difficulty]}`}>
                                    {selectedLesson.difficulty}
                                </span>
                            </div>
                            <div 
                                className="prose prose-slate max-w-none"
                                dangerouslySetInnerHTML={{ __html: md.render(lessonContent || '') }}
                            />
                             <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-dashed">
                                <h4 className="font-semibold text-slate-700">Instructional Video</h4>
                                <p className="text-sm text-slate-500 mt-1">A short video for this lesson is being generated and will appear here soon.</p>
                             </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrainingPage;
